import { useState, useCallback, useEffect, useRef } from "react";

const VISIBILITY_MEASUREMENT_THRESHOLD = 48;

const getChildVisibilityState = (element, tolerance) => {
  const containerRect = element.getBoundingClientRect();
  const firstChildRect = element.firstElementChild?.getBoundingClientRect?.();
  const lastChildRect = element.lastElementChild?.getBoundingClientRect?.();

  return {
    isFirstChildVisible: firstChildRect
      ? firstChildRect.left >= containerRect.left - tolerance
      : null,
    isLastChildVisible: lastChildRect
      ? lastChildRect.right <= containerRect.right + tolerance
      : null
  };
};

const getScrollMetrics = element => {
  const scrollLeft = Math.round(element.scrollLeft);
  const maxScrollLeft = Math.round(element.scrollWidth - element.clientWidth);
  const hasOverflow = maxScrollLeft > 1;
  const tolerance = 1;

  return {
    hasOverflow,
    isAtStart: scrollLeft <= tolerance,
    isAtEnd: scrollLeft >= maxScrollLeft - tolerance,
    scrollLeft,
    maxScrollLeft
  };
};

const shouldMeasureChildVisibility = ({ scrollLeft, maxScrollLeft }) =>
  scrollLeft <= VISIBILITY_MEASUREMENT_THRESHOLD ||
  maxScrollLeft - scrollLeft <= VISIBILITY_MEASUREMENT_THRESHOLD;

const useScrollState = (listRef, measurementDependency) => {
  const [scrollState, setScrollState] = useState({
    hasOverflow: false,
    isAtStart: true,
    isAtEnd: false,
    scrollLeft: 0,
    maxScrollLeft: 0
  });

  const scheduledRef = useRef(false);
  const timeoutIdRef = useRef(null);
  const rafIdRef = useRef(null);

  const updateScrollState = useCallback(() => {
    if (!listRef.current) return;
    const element = listRef.current;
    const tolerance = 1;
    const nextScrollState = getScrollMetrics(element);
    const { isFirstChildVisible, isLastChildVisible } = getChildVisibilityState(
      element,
      tolerance
    );
    setScrollState({
      ...nextScrollState,
      isAtStart: isFirstChildVisible ?? nextScrollState.isAtStart,
      isAtEnd: isLastChildVisible ?? nextScrollState.isAtEnd
    });
  }, [listRef]);

  const updateScrollStateFromScroll = useCallback(() => {
    if (!listRef.current) return;

    const element = listRef.current;
    const nextScrollState = getScrollMetrics(element);

    if (!shouldMeasureChildVisibility(nextScrollState)) {
      setScrollState(nextScrollState);

      return;
    }

    const tolerance = 1;
    const { isFirstChildVisible, isLastChildVisible } = getChildVisibilityState(
      element,
      tolerance
    );

    setScrollState({
      ...nextScrollState,
      isAtStart: isFirstChildVisible ?? nextScrollState.isAtStart,
      isAtEnd: isLastChildVisible ?? nextScrollState.isAtEnd
    });
  }, [listRef]);

  const throttledScrollHandler = useCallback(() => {
    if (!scheduledRef.current) {
      scheduledRef.current = true;
      rafIdRef.current = requestAnimationFrame(() => {
        updateScrollStateFromScroll();
        scheduledRef.current = false;
      });
    }
  }, [updateScrollStateFromScroll]);

  const debouncedResizeHandler = useCallback(() => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }
    timeoutIdRef.current = setTimeout(updateScrollState, 250);
  }, [updateScrollState]);

  useEffect(() => {
    const element = listRef.current;
    if (!element) return;

    // Initial state update
    updateScrollState();

    // Add event listeners
    element.addEventListener("scroll", throttledScrollHandler, {
      passive: true
    });
    window.addEventListener("resize", debouncedResizeHandler);

    return () => {
      element.removeEventListener("scroll", throttledScrollHandler);
      window.removeEventListener("resize", debouncedResizeHandler);
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [
    listRef.current, // Changed: now depends on the actual DOM element
    measurementDependency,
    throttledScrollHandler,
    debouncedResizeHandler,
    updateScrollState
  ]);

  return {
    ...scrollState,
    updateScrollState
  };
};

export default useScrollState;
